import React, { useState, useRef, useEffect } from 'react';
import Chip from "../kit/Chip";
import OptionCard from "../kit/OptionCard";
import Dropdown from "../kit/Dropdown";

// Day abbreviations for weekly repeat
const WEEKDAYS = [
  { id: 'sun', label: 'S', full: 'Sunday' },
  { id: 'mon', label: 'M', full: 'Monday' },
  { id: 'tue', label: 'T', full: 'Tuesday' },
  { id: 'wed', label: 'W', full: 'Wednesday' },
  { id: 'thu', label: 'T', full: 'Thursday' },
  { id: 'fri', label: 'F', full: 'Friday' },
  { id: 'sat', label: 'S', full: 'Saturday' },
];

// Full day names mapping (for EditEvent compatibility)
const FULL_WEEKDAYS = [
  'sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'
];

// Number to text mapping for frequency dropdown
const NUMBER_TO_TEXT = {
  1: 'single',
  2: 'other',
  3: 'third',
  4: 'fourth',
  5: 'fifth',
  6: 'sixth',
};

// Text to number mapping
const TEXT_TO_NUMBER = {
  'single': 1,
  'other': 2,
  'third': 3,
  'fourth': 4,
  'fifth': 5,
  'sixth': 6,
};

// Get ordinal suffix for a number (1st, 2nd, 3rd, etc.)
const getOrdinal = (n) => {
  const s = ['th', 'st', 'nd', 'rd'];
  const v = n % 100;
  return n + (s[(v - 20) % 10] || s[v] || s[0]);
};

// Get the week number of the month for a given date (1-5)
const getWeekOfMonth = (date) => {
  if (!date) return 1;
  const d = new Date(typeof date === 'string' ? date + 'T12:00:00' : date);
  return Math.ceil(d.getDate() / 7);
};

// Get the day name for a given date
const getDayName = (date) => {
  if (!date) return 'day';
  const d = new Date(typeof date === 'string' ? date + 'T12:00:00' : date);
  return WEEKDAYS[d.getDay()]?.full || 'day';
};

/**
 * RepeatingEventSettings - Industry-standard recurring event UI
 * Similar to Google Calendar with day/week/month options
 * 
 * Shared between EventPostingWizard and EditEvent
 * 
 * @param {Object} value - Repeating settings
 * @param {number} value.repeatEveryNumber - Repeat every N periods
 * @param {string} value.repeatEveryPeriod - 'day' | 'week' | 'month'
 * @param {string[]} value.repeatOn - Days selected for weekly repeat (short form: 'sun', 'mon', etc. or full: 'sunday', 'monday')
 * @param {string} value.monthlyType - 'dayOfMonth' | 'dayOfWeek'
 * @param {string} value.repeatEndsOn - End date (YYYY-MM-DD)
 * @param {Function} onChange - Callback when value changes
 * @param {Object} baseDate - Primary date info for context
 * @param {string} minEndDate - Minimum allowed end date
 * @param {boolean} useFullDayNames - Use full day names like 'sunday' instead of 'sun' (for EditEvent compatibility)
 */
const RepeatingEventSettings = React.memo(({ 
  value, 
  onChange, 
  baseDate, 
  minEndDate,
  useFullDayNames = false 
}) => {
  const weekOfMonth = getWeekOfMonth(baseDate?.startDate);
  const dayName = getDayName(baseDate?.startDate);
  const dayOfMonth = baseDate?.startDate ? new Date(typeof baseDate.startDate === 'string' ? baseDate.startDate + 'T12:00:00' : baseDate.startDate).getDate() : 1;

  const handleFrequencySelect = (num) => {
    onChange({ ...value, repeatEveryNumber: num });
  };

  // Get day identifier based on useFullDayNames setting
  const getDayId = (index) => {
    return useFullDayNames ? FULL_WEEKDAYS[index] : WEEKDAYS[index].id;
  };

  // Check if a day is selected (handles both short and full names)
  const isDaySelected = (dayIndex) => {
    const shortId = WEEKDAYS[dayIndex].id;
    const fullId = FULL_WEEKDAYS[dayIndex];
    return (value.repeatOn || []).includes(shortId) || (value.repeatOn || []).includes(fullId);
  };

  const toggleWeekday = (dayIndex) => {
    const currentDays = value.repeatOn || [];
    const dayId = getDayId(dayIndex);
    const shortId = WEEKDAYS[dayIndex].id;
    const fullId = FULL_WEEKDAYS[dayIndex];
    
    // Check if either form is in the array
    const isSelected = currentDays.includes(shortId) || currentDays.includes(fullId);
    
    let newDays;
    if (isSelected) {
      // Remove both forms just in case
      newDays = currentDays.filter(d => d !== shortId && d !== fullId);
    } else {
      newDays = [...currentDays, dayId];
    }
    
    onChange({ ...value, repeatOn: newDays });
  };

  // Calculate minimum end date (must be at least 1 day after start date)
  const parseLocalDate = (str) => typeof str === 'string' ? new Date(str + 'T12:00:00') : new Date(str);

  const calculateMinEndDate = () => {
    if (!baseDate?.startDate) return minEndDate || new Date().toISOString().split('T')[0];
    const startDate = parseLocalDate(baseDate.startDate);
    startDate.setDate(startDate.getDate() + 1);
    const y = startDate.getFullYear();
    const m = String(startDate.getMonth() + 1).padStart(2, '0');
    const d = String(startDate.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  };

  const isEndDateValid = () => {
    if (!value.repeatEndsOn || !baseDate?.startDate) return false;
    const startDate = parseLocalDate(baseDate.startDate);
    const endDate = parseLocalDate(value.repeatEndsOn);
    return endDate > startDate;
  };

  const minEndDateCalculated = calculateMinEndDate();
  const endDateValid = value.repeatEndsOn ? isEndDateValid() : false;

  return (
    <div className="inyc-panel is-sm recurrence-settings-card">
      {/* Frequency row */}
      <div className="flex items-center gap-kit-8 mb-kit-12 flex-wrap">
        <span className="inyc-caption">every</span>
        
        {/* Frequency dropdown */}
        <Dropdown
          variant="chip"
          aria-label="repeat frequency"
          value={value.repeatEveryNumber || 1}
          onChange={(v) => handleFrequencySelect(parseInt(v))}
          options={Object.entries(NUMBER_TO_TEXT).map(([num, text]) => ({ value: parseInt(num), label: text }))}
        />
        
        <div className="flex gap-kit-6">
          {['day', 'week', 'month'].map((period) => (
            <Chip
              key={period}
              selected={value.repeatEveryPeriod === period}
              onClick={() => onChange({ 
                ...value, 
                repeatEveryPeriod: period,
                // Reset related options when changing period
                repeatOn: period === 'week' ? [] : value.repeatOn,
                monthlyType: period === 'month' ? (value.monthlyType || 'dayOfMonth') : value.monthlyType,
              })}
            >
              {period}
            </Chip>
          ))}
        </div>
      </div>

      {/* Weekly: Day selector */}
      {value.repeatEveryPeriod === 'week' && (
        <div className="mb-kit-12">
          <div className="inyc-caption mb-kit-8">
            on these days
          </div>
          <div className="flex gap-kit-6">
            {WEEKDAYS.map((day, index) => (
              <Chip
                key={day.id}
                selected={isDaySelected(index)}
                onClick={() => toggleWeekday(index)}
                title={day.full}
                aria-label={day.full}
                className="!w-[1.75rem] justify-center !px-0"
              >
                {day.label}
              </Chip>
            ))}
          </div>
        </div>
      )}

      {/* Monthly: Day of month vs nth weekday */}
      {value.repeatEveryPeriod === 'month' && baseDate?.startDate && (
        <div className="mb-kit-12">
          <div className="inyc-caption mb-kit-8">
            repeat on
          </div>
          <div className="flex flex-col gap-kit-8">
            {/* Day of month option */}
            <OptionCard
              selected={value.monthlyType === 'dayOfMonth' || !value.monthlyType}
              onClick={() => onChange({ ...value, monthlyType: 'dayOfMonth' })}
              title={`same date each month (the ${getOrdinal(dayOfMonth)})`}
            />
            
            {/* Nth weekday option (e.g., "2nd Monday") */}
            <OptionCard
              selected={value.monthlyType === 'dayOfWeek'}
              onClick={() => onChange({ ...value, monthlyType: 'dayOfWeek' })}
              title={`same weekday each month (${getOrdinal(weekOfMonth)} ${dayName})`}
            />
          </div>
        </div>
      )}

      {/* End date */}
      <div className="flex flex-col gap-kit-8">
        <div className="flex items-center gap-kit-10">
          <span className="inyc-caption">until</span>
          <input
            type="date"
            value={value.repeatEndsOn || ''}
            onChange={(e) => onChange({ ...value, repeatEndsOn: e.target.value })}
            min={minEndDateCalculated}
            className={`inyc-field is-sm flex-1 ${value.repeatEndsOn && !endDateValid ? "is-error" : ""}`}
          />
        </div>
        
        {!value.repeatEndsOn && (
          <div className="ml-[3.5rem] text-kit-2xs text-danger">
            please select an end date (must be after the event start date)
          </div>
        )}
        {value.repeatEndsOn && !endDateValid && (
          <div className="ml-[3.5rem] text-kit-2xs text-danger">
            end date must be at least 1 day after the event start date
          </div>
        )}
      </div>
    </div>
  );
});

export default RepeatingEventSettings;

